import { safeAbsurd } from '@ipfabric/utils';
import { DiffLine, type DiffLineProps } from 'components/Diff/DiffLine';
import {
  type RowBlock,
  changesToRowBlocks,
  isChangedRowBlock,
  isUnchangedRowBlock,
} from 'components/Diff/helpers/changesToRowBlocks';
import type { Change } from 'diff';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { Fragment, type ReactNode } from 'react';

type DiffBlock = {
  removedStartLine: number;
  removedCount: number;
  addedStartLine: number;
  addedCount: number;
  diffLinesProps: DiffLineProps[];
};

const rowBlocksToDiffBlocks = (rowBlocks: RowBlock[]): DiffBlock[] => {
  let oldLineNumber = 1;
  let newLineNumber = 1;

  const diffBlocks: DiffBlock[] = [];
  for (const rowBlock of rowBlocks) {
    if (isUnchangedRowBlock(rowBlock)) {
      oldLineNumber += rowBlock.unchanged.length;
      newLineNumber += rowBlock.unchanged.length;
    } else if (isChangedRowBlock(rowBlock)) {
      const removedStartLine = oldLineNumber;
      const addedStartLine = newLineNumber;
      const diffLinesProps: DiffLineProps[] = [];
      for (const line of rowBlock.removed) {
        diffLinesProps.push({
          content: line,
          status: 'removed',
          lineNumber: oldLineNumber,
          inline: true,
        });
        oldLineNumber += 1;
      }
      for (const line of rowBlock.added) {
        diffLinesProps.push({
          content: line,
          status: 'added',
          newLineNumber,
          inline: true,
        });
        newLineNumber += 1;
      }
      diffBlocks.push({
        removedStartLine,
        removedCount: oldLineNumber - removedStartLine,
        addedStartLine,
        addedCount: newLineNumber - addedStartLine,
        diffLinesProps,
      });
    } else {
      safeAbsurd(rowBlock, 'Unknown row block type');
    }
  }

  return diffBlocks;
};

export const renderInlineDiffChanges = (changes: Change[]): ReactNode[] =>
  pipe(
    changes,
    changesToRowBlocks,
    rowBlocksToDiffBlocks,
    array.mapWithIndex((index, diffBlock) => (
      <Fragment key={index}>
        <div className='diff-patch-lines'>
          {diffBlock.removedCount > 0 &&
            `-${diffBlock.removedStartLine},${diffBlock.removedCount}`}{' '}
          {diffBlock.addedCount > 0 &&
            `+${diffBlock.addedStartLine},${diffBlock.addedCount}`}
        </div>
        {diffBlock.diffLinesProps.map((diffLineProps, diffLineIndex) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: no better key
          <DiffLine key={diffLineIndex} {...diffLineProps} />
        ))}
      </Fragment>
    )),
  );
